import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const CookieBannerContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${props => props.theme.colors.primary};
  color: white;
  padding: 1.5rem;
  z-index: 1000;
  display: ${props => props.show ? 'block' : 'none'};
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
`;

const CookieContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const CookieText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  
  a {
    color: ${props => props.theme.colors.accent};
    text-decoration: underline;
    
    &:hover {
      color: white;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const CookieButton = styled.button`
  background-color: ${props => props.primary ? props.theme.colors.accent : 'transparent'};
  color: white;
  border: 2px solid ${props => props.primary ? 'transparent' : 'white'};
  padding: 0.5rem 1.5rem;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.primary ? props.theme.colors.accentDark : 'white'};
    color: ${props => props.primary ? 'white' : props.theme.colors.primary};
  }
`;

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookies, setCookies] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      setShowBanner(true);
    }
  }, []);

  const handleAcceptAll = () => {
    setCookies({
      necessary: true,
      analytics: true,
      marketing: true
    });
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      analytics: true,
      marketing: true
    }));
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(cookies));
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    setCookies({
      necessary: true,
      analytics: false,
      marketing: false
    });
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      analytics: false,
      marketing: false
    }));
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <CookieBannerContainer show={showBanner}>
      <CookieContent>
        <CookieText>
          We use cookies to enhance your experience on our website. Some cookies are necessary for the website to function, 
          while others help us understand how you interact with our site. By clicking "Accept All", you consent to the use of all cookies. 
          You can manage your preferences by clicking "Cookie Settings". For more information, please read our{' '}
          <Link to="/cookie-policy">Cookie Policy</Link>.
        </CookieText>
        <ButtonGroup>
          <CookieButton onClick={handleAcceptAll} primary>
            Accept All
          </CookieButton>
          <CookieButton onClick={() => setShowSettings(!showSettings)}>
            Cookie Settings
          </CookieButton>
          <CookieButton onClick={handleRejectAll}>
            Reject All
          </CookieButton>
        </ButtonGroup>
      </CookieContent>
      
      {showSettings && (
        <CookieContent style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
          <div style={{ width: '100%' }}>
            <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>Cookie Settings</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h4 style={{ margin: 0, fontSize: '1rem' }}>Necessary Cookies</h4>
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', opacity: 0.8 }}>
                    Required for the website to function properly
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={cookies.necessary}
                  disabled
                  style={{ width: '20px', height: '20px' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h4 style={{ margin: 0, fontSize: '1rem' }}>Analytics Cookies</h4>
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', opacity: 0.8 }}>
                    Help us understand how visitors interact with our website
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={cookies.analytics}
                  onChange={(e) => setCookies({ ...cookies, analytics: e.target.checked })}
                  style={{ width: '20px', height: '20px' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h4 style={{ margin: 0, fontSize: '1rem' }}>Marketing Cookies</h4>
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', opacity: 0.8 }}>
                    Used to track visitors across websites for advertising purposes
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={cookies.marketing}
                  onChange={(e) => setCookies({ ...cookies, marketing: e.target.checked })}
                  style={{ width: '20px', height: '20px' }}
                />
              </div>
            </div>
            <ButtonGroup style={{ marginTop: '1rem', justifyContent: 'flex-end' }}>
              <CookieButton onClick={handleSavePreferences} primary>
                Save Preferences
              </CookieButton>
            </ButtonGroup>
          </div>
        </CookieContent>
      )}
    </CookieBannerContainer>
  );
};

export default CookieBanner; 